/**
 * Centralized logging utility for the application
 * Provides configurable logging levels and categories
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

export enum LogCategory {
  AUTH = 'AUTH',
  WEBSOCKET = 'WEBSOCKET',
  API = 'API',
  DATABASE = 'DATABASE',
  REACTIONS = 'REACTIONS',
  HAZARDS = 'HAZARDS',
  COMMENTS = 'COMMENTS',
  GENERAL = 'GENERAL'
}

interface LogConfig {
  level: LogLevel;
  categories: Set<LogCategory>;
  enableTimestamps: boolean;
  enableEmojis: boolean;
}

class Logger {
  private config: LogConfig = {
    level: LogLevel.WARN, // Default to WARN level to reduce noise
    categories: new Set(Object.values(LogCategory)), // Enable all categories by default
    enableTimestamps: true,
    enableEmojis: true
  };

  private getEmoji(category: LogCategory): string {
    if (!this.config.enableEmojis) return '';
    
    const emojis: Record<LogCategory, string> = {
      [LogCategory.AUTH]: '🔐',
      [LogCategory.WEBSOCKET]: '📡',
      [LogCategory.API]: '🌐',
      [LogCategory.DATABASE]: '💾',
      [LogCategory.REACTIONS]: '👍',
      [LogCategory.HAZARDS]: '⚠️',
      [LogCategory.COMMENTS]: '💬',
      [LogCategory.GENERAL]: '📝'
    };
    
    return emojis[category] || '📝';
  }

  private formatMessage(level: LogLevel, category: LogCategory, message: string, data?: any): string {
    const timestamp = this.config.enableTimestamps ? `[${new Date().toISOString()}] ` : '';
    const emoji = this.getEmoji(category);
    const levelName = LogLevel[level];
    
    return `${timestamp}${emoji} ${category} ${levelName}: ${message}`;
  }

  private shouldLog(level: LogLevel, category: LogCategory): boolean {
    return level <= this.config.level && this.config.categories.has(category);
  }

  public setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  public setCategories(categories: LogCategory[]): void {
    this.config.categories = new Set(categories);
  }

  public enableCategory(category: LogCategory): void {
    this.config.categories.add(category);
  }

  public disableCategory(category: LogCategory): void {
    this.config.categories.delete(category);
  }

  public setTimestamps(enabled: boolean): void {
    this.config.enableTimestamps = enabled;
  }

  public setEmojis(enabled: boolean): void {
    this.config.enableEmojis = enabled;
  }

  public error(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.ERROR, category)) {
      console.error(this.formatMessage(LogLevel.ERROR, category, message), data || '');
    }
  }

  public warn(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN, category)) {
      console.warn(this.formatMessage(LogLevel.WARN, category, message), data || '');
    }
  }

  public info(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO, category)) {
      console.info(this.formatMessage(LogLevel.INFO, category, message), data || '');
    }
  }

  public debug(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG, category)) {
      console.log(this.formatMessage(LogLevel.DEBUG, category, message), data || '');
    }
  }

  // Convenience methods for common use cases
  public authError(message: string, data?: any): void {
    this.error(LogCategory.AUTH, message, data);
  }

  public authWarn(message: string, data?: any): void {
    this.warn(LogCategory.AUTH, message, data);
  }

  public websocketError(message: string, data?: any): void {
    this.error(LogCategory.WEBSOCKET, message, data);
  }

  public websocketWarn(message: string, data?: any): void {
    this.warn(LogCategory.WEBSOCKET, message, data);
  }

  public websocketInfo(message: string, data?: any): void {
    this.info(LogCategory.WEBSOCKET, message, data);
  }

  public apiError(message: string, data?: any): void {
    this.error(LogCategory.API, message, data);
  }

  public apiWarn(message: string, data?: any): void {
    this.warn(LogCategory.API, message, data);
  }

  public reactionsInfo(message: string, data?: any): void {
    this.info(LogCategory.REACTIONS, message, data);
  }

  public reactionsWarn(message: string, data?: any): void {
    this.warn(LogCategory.REACTIONS, message, data);
  }

  public hazardsInfo(message: string, data?: any): void {
    this.info(LogCategory.HAZARDS, message, data);
  }

  public hazardsWarn(message: string, data?: any): void {
    this.warn(LogCategory.HAZARDS, message, data);
  }

  public generalError(message: string, data?: any): void {
    this.error(LogCategory.GENERAL, message, data);
  }

  public generalWarn(message: string, data?: any): void {
    this.warn(LogCategory.GENERAL, message, data);
  }

  public generalInfo(message: string, data?: any): void {
    this.info(LogCategory.GENERAL, message, data);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for easy access
export default logger;
